// src/adminFlow/AdminHomePage.js
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API_BASE from '../apiBase';
import useAutoRefresh from './useAutoRefresh';
import { useListenInvalidation } from './adminInvalidation';
import {
  Loader2,
  Package,
  ShoppingCart,
  DollarSign,
  Users,
  AlertTriangle,
  Plus,
  LogOut,
} from 'lucide-react';

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
      <div className="flex items-center justify-between">
        <div className="p-2 rounded-xl bg-orange-50">
          <Icon className="w-5 h-5 text-orange-600" />
        </div>
        {sub ? <span className="text-xs text-gray-400">{sub}</span> : null}
      </div>
      <div className="mt-4 text-2xl font-semibold text-gray-800">{value}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <section className="bg-white rounded-2xl shadow-sm border border-gray-100">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">{title}</h3>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

export default function AdminHomePage() {
  const [loading, setLoading] = useState(true);
  const [pingMsg, setPingMsg] = useState('');
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState(null);
  const navigate = useNavigate();

  async function handleLogout() {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {}
    navigate('/signin', { replace: true });
  }

  // central reload used by timers/focus/invalidation
  const reload = useCallback(async () => {
    try {
      const [ovr, ord] = await Promise.all([
        fetch(`${API_BASE}/api/admin/overview`, { credentials: 'include', cache: 'no-store' })
          .then(r => (r.ok ? r.json() : null))
          .catch(() => null),
        fetch(`${API_BASE}/api/admin/orders?limit=5`, { credentials: 'include', cache: 'no-store' })
          .then(r => (r.ok ? r.json() : null))
          .catch(() => null),
      ]);
      setStats(ovr);
      setRecent(ord?.orders || null);
    } catch {}
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/admin/ping`, { credentials: 'include' });
        const j = await r.json();
        if (!alive) return;
        setPingMsg(r.ok ? j.message || 'OK' : (j.error || 'Not authorized'));
      } catch {
        if (alive) setPingMsg('Could not reach admin API');
      }

      try {
        if (alive) await reload();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [reload]);

  // Auto refresh every 30s + on tab focus
  useAutoRefresh(reload, { intervalMs: 30000, refetchOnFocus: true });
  // Listen for cross-tab invalidations
  useListenInvalidation(() => { reload(); });

  const derived = useMemo(() => {
    const currency = stats?.currency || 'CAD';
    const fmt = (n) => {
      try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(n || 0)); }
      catch { return `$${Number(n || 0).toFixed(2)}`; }
    };
    return {
      salesToday: stats?.sales_today != null ? fmt(stats.sales_today) : '—',
      ordersToday: stats?.orders_today ?? '—',
      lowStock: stats?.low_stock ?? '—',
      activeUsers: stats?.active_users ?? '—',
      threshold: stats?.threshold ?? 5,
    };
  }, [stats]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-orange-500 to-red-500" />
            <div>
              <div className="text-lg font-semibold text-gray-800">SodaSips Admin</div>
              <div className="text-[11px] text-gray-500">Portal</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="text-sm px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              View Storefront
            </Link>
            <button
              onClick={handleLogout}
              className="text-sm px-3 py-2 rounded-lg bg-gray-800 text-white hover:bg-black inline-flex items-center gap-2"
              title="Log out"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Welcome back, Admin 👋</h1>
          <p className="text-gray-600 mt-1">
            System status:&nbsp;
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-orange-500" /> Checking…
              </span>
            ) : (
              <span className={`font-medium ${/ok|ready|success/i.test(pingMsg) ? 'text-green-700' : 'text-gray-800'}`}>
                {pingMsg}
              </span>
            )}
          </p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={DollarSign} label="Sales Today" value={derived.salesToday} sub="gross" />
          <StatCard icon={ShoppingCart} label="Orders Today" value={derived.ordersToday} />
          <StatCard
            icon={Package}
            label={`Low-Stock Items (≤ ${derived.threshold})`}
            value={derived.lowStock}
          />
          <StatCard icon={Users} label="Active Users" value={derived.activeUsers} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Orders */}
          <div className="lg:col-span-2">
            <Section
              title="Recent Orders"
              action={<Link to="/admin/orders" className="text-sm text-orange-600 hover:underline">View all</Link>}
            >
              {!recent ? (
                <div className="text-sm text-gray-500">
                  Add <code className="bg-gray-50 px-1 rounded">GET /api/admin/orders?limit=5</code> to populate this table.
                </div>
              ) : recent.length === 0 ? (
                <div className="text-sm text-gray-500">No recent orders.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-2 pr-4">Order #</th>
                        <th className="py-2 pr-4">Customer</th>
                        <th className="py-2 pr-4">Total</th>
                        <th className="py-2 pr-4">Status</th>
                        <th className="py-2">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent.map((o) => (
                        <tr key={o.id} className="border-b last:border-none">
                          <td className="py-2 pr-4 font-medium text-gray-800">#{o.id}</td>
                          <td className="py-2 pr-4">{o.customer_email || '—'}</td>
                          <td className="py-2 pr-4">{o.total_formatted ?? o.total}</td>
                          <td className="py-2 pr-4">
                            <span className="px-2 py-1 rounded-full text-xs bg-orange-50 text-orange-700 border border-orange-100">
                              {o.status || 'Paid'}
                            </span>
                          </td>
                          <td className="py-2">
                            {o.order_date ? new Date(o.order_date).toLocaleString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>

                  </table>
                </div>
              )}
            </Section>
          </div>

          {/* Right column */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Section title="Quick Actions">
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to="/admin/products/new"
                  className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50/40"
                >
                  <Plus className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-gray-800">Add Product</span>
                </Link>
                <Link
                  to="/admin/products"
                  className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50/40"
                >
                  <Package className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-gray-800">Manage Products</span>
                </Link>
                <Link
                  to="/admin/orders"
                  className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50/40"
                >
                  <ShoppingCart className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-gray-800">Manage Orders</span>
                </Link>
                <Link
                  to="/admin/users"
                  className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50/40"
                >
                  <Users className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-gray-800">Manage Users</span>
                </Link>
              </div>
            </Section>

            {/* Inventory Alerts */}
            <Section title="Inventory Alerts">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-yellow-50">
                  <AlertTriangle className="w-4 h-4 text-yellow-700" />
                </div>
                <div className="text-sm text-gray-700">
                  {stats?.low_stock_list?.length ? (
                    <>
                      <div className="font-medium mb-1">
                        Low stock variants (≤ {derived.threshold}):
                      </div>
                      <ul className="list-disc ml-5 space-y-0.5">
                        {stats.low_stock_list.slice(0, 5).map((s) => (
                          <li key={s.sku}>
                            {s.name} — <span className="text-gray-500">{s.sku}</span> ({s.stock_qty} left)
                          </li>
                        ))}
                      </ul>
                      {stats.low_stock_list.length > 5 ? (
                        <div className="text-xs text-gray-500 mt-2">
                          And {stats.low_stock_list.length - 5} more…
                        </div>
                      ) : null}
                    </>
                  ) : (
                    'No low-stock alerts (or endpoint not set up yet).'
                  )}
                </div>
              </div>
            </Section>
          </div>
        </div>
      </main>
    </div>
  );
}
